import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import PhoneIconFilled from "../../assets/svg/v1/phone-icon-filled";
import BackspaceFilledIcon from "../../assets/svg/v1/backspace-filled-icon";
import PhoneNumberCountryCodeSelector from "../PhoneNumberCountryCodeSelector/PhoneNumberCountryCodeSelector";
import { countryOptions } from "../../utils/constants/countryOptions";
import { transformCountryOptions } from "../PhoneNumberCountryCodeSelector/utils/phoneNumberUtils";
import { CountryOption } from "../PhoneNumberCountryCodeSelector/utils/types";
import Button from "../Button/Button";
import CloseIcon from "../../assets/svg/close-icon";
import {
  getRegionCallingCode,
  getRegionFlag,
} from "../../utils/constants/regionDefaultCallingCodes";

interface DialerModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  dialerNumber: string;
  setDialerNumber: (number: string) => void;
  setCountryCode: (code: string) => void;
  handleMakeCall: () => void;
  initialCountryCode?: string;
  className?: string;
}

const DialerModal: React.FC<DialerModalProps> = ({
  isOpen,
  onOpenChange,
  dialerNumber,
  setDialerNumber,
  setCountryCode,
  handleMakeCall,
  initialCountryCode,
  className,
}) => {
  const isDarkMode = useSelector(
    (state: RootState) => state?.themeProvider?.isDarkMode
  );
  const client = useSelector((state: RootState) => state?.auth?.client);

  const transformedCountryOptions = transformCountryOptions(countryOptions);

  const defaultCountryCode = getRegionCallingCode(client?.region);

  const [countryCodeValue, setCountryCodeValue] = useState(
    initialCountryCode && initialCountryCode.trim() !== ""
      ? initialCountryCode.startsWith("+")
        ? initialCountryCode
        : `+${initialCountryCode}`
      : defaultCountryCode
  );

  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(
    () => {
      const initialCode = countryCodeValue.replace("+", "");
      return (
        transformedCountryOptions.find(
          (country) =>
            country.dialCode === `+${initialCode}` &&
            // extra handling for US since Canada also uses +1 and gets selected first
            (client?.region === "US"
              ? country?.flag?.toLowerCase() ===
                getRegionFlag(client?.region)?.toLowerCase()
              : true)
        ) || null
      );
    }
  );

  const inputRef = useRef<HTMLInputElement>(null);

  const handleNumberInput = (value: string) => {
    if (dialerNumber?.length < 15) {
      setDialerNumber(dialerNumber + value);
    }
    inputRef?.current?.focus?.();
  };

  const handleBackspace = () => {
    setDialerNumber(dialerNumber?.slice?.(0, -1));
    inputRef?.current?.focus?.();
  };

  const handleClose = useCallback(() => {
    setDialerNumber("");
    setCountryCode("");
    onOpenChange(false);
  }, [setDialerNumber, setCountryCode, onOpenChange]);

  const handleMakeCallAndClose = () => {
    if (dialerNumber && countryCodeValue) {
      // Ensure country code is properly formatted before making the call
      setCountryCode(
        countryCodeValue.startsWith("+")
          ? countryCodeValue
          : `+${countryCodeValue}`
      );
      handleMakeCall();
      handleClose();
    }
  };

  useEffect(() => {
    setCountryCode(countryCodeValue);
  }, [countryCodeValue, setCountryCode]);

  useEffect(() => {
    const focusInput = setTimeout(() => {
      inputRef?.current?.focus?.();
    }, 100);
    return () => clearTimeout(focusInput);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      try {
        const target = event.target as Element;
        const isPopupContent = target?.closest?.("[data-dialer-modal]");
        const dialerButton = target?.closest?.("[data-dialer-button]");

        if (!isPopupContent && !dialerButton) {
          handleClose();
        }
      } catch (error) {
        console.error("Error in handleClickOutside:", error);
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-99 flex items-center justify-center bg-black/80">
      <div
        data-dialer-modal
        className={`h-fit w-72 bg-fixed-white dark:bg-popper-bg-dk backdrop-blur-[10px] rounded-lg shadow-popper-blur-effect border border-border-gray dark:border-border-gray-dk dialer-modal ${className}`}
      >
        <div className="pt-[14px] pb-[22px]">
          <div className="flex justify-between items-center mx-4 mb-[14px]">
            <span className="text-md font-semibold text-heading-text dark:text-heading-text-dk">
              Make a Call
            </span>

            <Button
              Icon={CloseIcon}
              iconClass="w-[6px] h-[6px] text-verba-indigo dark:text-verba-indigo-dk"
              buttonContainerClass="w-[13px] h-[13px] bg-gray-100 dark:bg-gray-100-dk rounded-full flex flex-col items-center justify-center z-10"
              innerTextClass="text-verba-field-label dark:text-heading-text-dk text-md mr-2"
              onClick={handleClose}
            />
          </div>
          <div className="flex items-center mx-4">
            <PhoneNumberCountryCodeSelector
              countryOptions={transformedCountryOptions}
              selectedCountry={selectedCountry}
              onCountryChange={(country) => {
                setSelectedCountry(country);
                setCountryCodeValue(country.dialCode);
              }}
              phoneInput={dialerNumber}
              onPhoneInputChange={(e) => setDialerNumber(e.target.value)}
              placeholder="Enter phone number"
              containerClassName="dialer-phone-input"
              phoneInputClassName="!text-verba-field-label dark:!text-heading-text-dk !border-none"
              countryDropdownPlaceholder="Country"
              countryDropdownSearchPlaceholder="Search countries..."
              countryDropdownTriggerClassName="!border-none"
            />
          </div>

          <div className="grid grid-cols-3 gap-y-[11px] mt-6 mx-7 justify-items-center">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, "+", 0, "#"].map((digit) => (
              <button
                key={digit}
                onClick={() => handleNumberInput(digit?.toString())}
                className="bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk  font-medium rounded-full w-11 h-11 text-[1.375rem] text-verba-field-label dark:text-heading-text-dk hover:bg-verba-indigo/80 dark:hover:bg-tag-gray-dk/80 active:bg-verba-indigo/60 dark:active:bg-tag-gray-dk/60 transition-colors"
              >
                {digit}
              </button>
            ))}
          </div>
          <div className="flex justify-center items-center relative mt-4">
            <button
              onClick={handleMakeCallAndClose}
              disabled={!dialerNumber || dialerNumber?.length < 10}
              className="bg-verba-green-success w-11 h-11 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PhoneIconFilled className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={handleBackspace}
              className="absolute right-7 top-[11px]"
            >
              <BackspaceFilledIcon
                className={`w-5 h-5 ${
                  isDarkMode ? "text-verba-indigo" : "text-fixed-white-dk"
                } ${
                  !dialerNumber ? "opacity-50 cursor-default" : "cursor-pointer"
                }`}
                fill={isDarkMode ? "#EDEDED" : "#828282"}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DialerModal;
